import React from 'react';
import { t } from '@lingui/core/macro';

import { Box } from '@rover/kibble/core';
import { FilterChipGroup } from '@rover/kibble/patterns/chips/FilterChipGroup';
import {
  CatPreferences,
  DogPreferences,
} from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Preferences';
import { shouldShowControls } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { usePetInfoCalloutsExperiment } from '@rover/react-lib/src/pages/search/SearchPage/utilities/usePetInfoCalloutsExperiment';
import { WizardPageType } from '@rover/react-lib/src/pages/search/SearchPage/WizardPageType';
import type { ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200SettingsDogSizeBuckets';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import PetSize from '@rover/shared/js/search/components/PetSize';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType } from '@rover/types/src/ServiceType';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { SearchInterstitialUpdateSize } from '../../Filters/Collections/Wizard/analyticsEvents';

export type Props = {
  searchFilters: SearchFilters;
  onChange: (filters: Partial<SearchFilters>) => void;
  serviceTypeFilterOptions: SearchFiltersOptions;
  petSizeBuckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets;
  weightUnit: string;
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  hasPets: boolean;
  isCatOnly: boolean;
};

const PetPreferences = ({
  searchFilters,
  onChange,
  serviceTypeFilterOptions,
  petSizeBuckets,
  weightUnit,
  getServiceTypeBySlug,
  hasPets,
  isCatOnly,
}: Props): JSX.Element | null => {
  const brandAvailablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandAvailablePets);
  const serviceType = getServiceTypeBySlug(searchFilters.serviceType);
  const { isActive: inPetInfoCalloutsExperiment } = usePetInfoCalloutsExperiment();
  const isFiltersM2 = useFlag(FLAGS.ROLLOUT_MOBILE_FILTERS_M2);

  const { showPetSize, showCatPreferences, showDogPreferences } = shouldShowControls({
    hasPets,
    isCatOnly: isCatOnly || isCatOnlyBrand,
    pageType: WizardPageType.DEFAULT,
    serviceOptions: serviceTypeFilterOptions,
    serviceType: serviceType || undefined,
    serviceTypeSlug: searchFilters.serviceType,
    inPetInfoCalloutsExperiment,
  });

  const handlePetSizeLegacyChange = (sizeKey: string | number): void => {
    onChange({ [sizeKey]: !searchFilters[sizeKey] });
    emitAnalyticsEvent(
      new SearchInterstitialUpdateSize({
        smallDogs: searchFilters.smallDogs,
        mediumDogs: searchFilters.mediumDogs,
        largeDogs: searchFilters.largeDogs,
        giantDogs: searchFilters.giantDogs,
        [sizeKey]: !searchFilters[sizeKey],
      })
    );
  };

  const { sizeKeys, petSizeOptions } = React.useMemo(() => {
    const keys = Object.keys(petSizeBuckets);
    return {
      sizeKeys: keys.map((key) => `${key}Dogs`),
      petSizeOptions: keys.map((key) => ({
        id: `${key}Dogs`,
        label: petSizeBuckets[key],
        value: `${key}Dogs`,
      })),
    };
  }, [petSizeBuckets]);

  const selectedSizeValues = sizeKeys.filter((key) => searchFilters[key]);

  const handlePetSizeChange = React.useCallback(
    (selectedValues: Array<string | number> | (string | number) | null): void => {
      const selected = [selectedValues].flat().filter(Boolean);
      const updates: Partial<SearchFilters> = {};
      sizeKeys.forEach((key) => (updates[key] = selected.includes(key)));
      onChange(updates);

      emitAnalyticsEvent(
        new SearchInterstitialUpdateSize({
          smallDogs: selected.includes('smallDogs'),
          mediumDogs: selected.includes('mediumDogs'),
          largeDogs: selected.includes('largeDogs'),
          giantDogs: selected.includes('giantDogs'),
        })
      );
    },
    [sizeKeys, onChange]
  );

  // Only render if at least one filter should be shown
  if (!showPetSize && !showCatPreferences && !showDogPreferences) {
    return null;
  }

  return (
    <Box pt="4x">
      {showPetSize && (
        <Box mb={showCatPreferences || showDogPreferences ? '4x' : undefined}>
          {isFiltersM2 ? (
            <FilterChipGroup
              groupLabel={t`What size are your dogs (${weightUnit})?`}
              groupSecondaryLabel={t`Select all that apply`}
              options={petSizeOptions}
              selectedValues={selectedSizeValues}
              canSelectMultiple
              shouldWrap
              onChange={handlePetSizeChange}
            />
          ) : (
            <PetSize
              displayAsIconButtons={false}
              label={t`What size are your dogs (${weightUnit})?`}
              searchFilters={searchFilters}
              weightUnit={weightUnit}
              onChange={handlePetSizeLegacyChange}
              petSizeBuckets={petSizeBuckets}
            />
          )}
        </Box>
      )}
      {showCatPreferences && (
        <Box mb={showDogPreferences ? '4x' : undefined}>
          <CatPreferences
            displayAsToggle
            onChange={(noCats) => onChange({ noCats })}
            value={searchFilters.noCats}
          />
        </Box>
      )}
      {showDogPreferences && (
        <Box>
          <DogPreferences
            displayAsToggle
            onChange={(personDoesNotHaveDogs) => onChange({ personDoesNotHaveDogs })}
            value={searchFilters.personDoesNotHaveDogs}
          />
        </Box>
      )}
    </Box>
  );
};

export default PetPreferences;
